import { Filter, RefreshCw, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../api";
import { DataTable } from "../components/DataTable";
import { DetailDrawer } from "../components/DetailDrawer";
import { PageHeader } from "../components/PageHeader";
import { Pagination } from "../components/Pagination";
import type { CollectionResponse, DataRecord, Session } from "../types";

export function TasksPage({ session }: { session: Session }) {
  const [rows, setRows] = useState<DataRecord[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: "", provider: "", capability: "", model: "" });
  const [applied, setApplied] = useState(filters);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<DataRecord | null>(null);
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState("");
  const pageSize = 10;
  const query = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    for (const [key, value] of Object.entries(applied)) if (value.trim()) params.set(key, value.trim());
    return params.toString();
  }, [applied, page]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiRequest<CollectionResponse>(`/api/ai-router/tasks?${query}`);
      setRows(Array.isArray(response.tasks) ? response.tasks.filter(isRecord) : []);
      setTotal(typeof response.total === "number" ? response.total : 0);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load tasks");
    } finally {
      setLoading(false);
    }
  }, [query]);
  useEffect(() => { void load(); }, [load]);

  async function retrieve(row: DataRecord) {
    const id = String(row.id ?? "");
    setActionId(id);
    setError("");
    try {
      await apiRequest(`/api/ai-router/tasks/${encodeURIComponent(id)}`, { method: "POST" }, session.csrfToken);
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not retrieve task");
    } finally {
      setActionId("");
    }
  }

  return <>
    <PageHeader title="AI Router Tasks" description="Persisted asynchronous tasks and terminal provider results." onRefresh={() => void load()} refreshing={loading} />
    <form className="filter-bar" onSubmit={(event) => { event.preventDefault(); setPage(1); setApplied(filters); }}>
      <Filter size={17} />
      {(["status", "provider", "capability", "model"] as const).map((key) => <label key={key}><span>{key[0]?.toUpperCase()}{key.slice(1)}</span><input value={filters[key]} onChange={(event) => setFilters((current) => ({ ...current, [key]: event.target.value }))} /></label>)}
      <button className="secondary-button" type="submit"><Search size={16} />Apply</button>
    </form>
    {error ? <div className="error-banner">{error}</div> : null}
    <section className="data-surface">
      {loading && rows.length === 0 ? <div className="loading-state">Loading tasks…</div> : <DataTable rows={rows} columns={[{ key: "id", label: "Task" }, { key: "status", kind: "status" }, { key: "publicModel", label: "Model" }, { key: "provider" }, { key: "capability" }, { key: "createdAt", label: "Created", kind: "date" }]} rowKey={(row) => String(row.id)} onInspect={setSelected} actions={(row) => ["queued", "in_progress"].includes(String(row.status)) ? <button className="table-action" type="button" disabled={actionId === row.id} onClick={() => void retrieve(row)}><RefreshCw size={15} className={actionId === row.id ? "spin" : ""} />Retrieve</button> : null} />}
      <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
    </section>
    <DetailDrawer title="Task" value={selected} onClose={() => setSelected(null)} />
  </>;
}

function isRecord(value: unknown): value is DataRecord { return Boolean(value) && typeof value === "object" && !Array.isArray(value); }
